/**
 * Root layout route — wraps the entire app.
 *
 * Responsibilities:
 * - Light mode is default per UX spec §2.3 (older, non-techy trust holders).
 *   No class on <html>; dark-mode tokens remain in @theme for a future toggle.
 * - Provides AuthProvider context.
 * - Resolves tenant slug from host and makes it available to child routes.
 * - F-FIRM-014 (Batch 16, 2026-05-20) — fetches the public branding overlay
 *   for the resolved slug (if any) and applies it to the document: CSS
 *   variables on `<html>`, document.title, favicon. Applied BEFORE
 *   sign-in so a tenant subdomain visitor sees the firm's branding from
 *   first paint.
 * - Renders TanStack Router devtools in dev mode only.
 * - Mounts the branded `notFoundComponent` + `errorComponent` (Task 3.20) so
 *   the user never sees TanStack Router's default unstyled fallbacks.
 */

import { useEffect, useRef } from 'react';
import { createRootRoute, Outlet, useLocation } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import { AuthProvider } from '../lib/auth-context';
import { resolveTenantSlugFromHost } from '../lib/tenant';
import { ToastRegion, ToastRouteWatcher } from '../components/ui/toast';
import { StepUpInterceptor } from '../lib/api';
import {
  applyBrandingToRoot,
  clearBrandingFromRoot,
  cacheBranding,
} from '../lib/branding-runtime';
import { useActiveBranding, resolveHostSlug } from '../lib/use-active-branding';
import type { ActiveBranding } from '../lib/use-active-branding';
import { useNeutralBrandingGuard } from '../lib/use-neutral-branding-guard';
import { StepUpModalHost } from '../components/auth/step-up-modal';
import { NotFound } from '../components/error-pages/not-found';
import { RouteError } from '../components/error-pages/route-error';
import { CookieConsentBanner } from '../components/cookie-consent/cookie-consent-banner';

export const Route = createRootRoute({
  // Tenant slug resolved once at the root — available to all child routes via context.
  beforeLoad() {
    const slug = resolveTenantSlugFromHost(
      window.location.host,
      new URLSearchParams(window.location.search),
    );
    return { tenantSlug: slug };
  },
  component: RootLayout,
  // Branded fallbacks. Both render inside the AuthProvider tree because
  // TanStack Router renders these as the root route's component — the
  // provider above is still mounted, so any future link to a signed-in
  // affordance from the fallback works without re-wrapping.
  notFoundComponent: () => <NotFound />,
  errorComponent: ({ error }) => <RouteError error={error} />,
});

function RootLayout() {
  // Light is default per UX spec §2.3. No class manipulation needed on <html>.
  // Dark-mode tokens stay in @theme for a future user-preference toggle (Phase 9.x).
  //
  // CookieConsentBanner self-gates on getConsent() — renders null after the
  // visitor has decided (and re-renders after a POLICY_VERSION bump). Mounted
  // at root so it shows on every route, including marketing pages where the
  // visitor isn't authenticated. Task 5.6 of the 2026-05-16 plan.
  return (
    <AuthProvider>
      <TenantBrandingOverlay />
      <StepUpInterceptor />
      <StepUpModalHost />
      <Outlet />
      <ToastRegion />
      {/* Empties the toast stack on every route change (round-11 P0). A
          sibling of <Outlet/>, so it re-renders on a client-side navigation
          through its own useLocation subscription, and ToastRegion itself
          stays router-free. */}
      <ToastRouteWatcher />
      <CookieConsentBanner />
      {import.meta.env.DEV && <TanStackRouterDevtools />}
    </AuthProvider>
  );
}

/**
 * F-FIRM-014 / white-label runtime — applies the active firm branding (host
 * subdomain OR the logged-in user's tenant) to the WHOLE app: the brand CSS
 * variable ramps on `<html>`, the page title, and the favicon. Renders no DOM.
 *
 * The colour ramps are derived at runtime (see `branding-runtime.ts`) and
 * override the REAL consumed tokens (`--color-primary-*` / `--color-accent-*` /
 * `--color-brand-*`), so every surface reskins — not just the auth screen.
 *
 * Why a component vs inlining in RootLayout: keeps `useEffect` rules
 * compositional and lets the cross-tenant test mount it directly without
 * spinning up the entire router tree.
 */
/** Surfaces that ARE the product, where a logged-in user's firm branding is
 *  wanted on a host with no tenant subdomain. Anything else on such a host is
 *  public marketing and stays neutral Elreign. */
const PRODUCT_SURFACE_PREFIXES = [
  '/app', '/admin', '/superadmin', '/audit', '/onboarding', '/auth', '/partner',
];

export function isProductSurfacePath(pathname: string): boolean {
  return PRODUCT_SURFACE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * The whole branding decision as one pure function: which of the two overlays
 * wins, and whether it is allowed to paint on THIS surface.
 *
 * Extracted 2026-08-23 so the decision is unit-testable without a router or a
 * query client. It is the piece that has now failed twice in a row (the
 * marketing site wearing a customer skin, then the product NOT wearing the
 * firm skin), and both times the failure was silent — nothing throws, the page
 * simply renders the wrong palette.
 */
export function resolveEffectiveBranding(input: {
  branding: ActiveBranding | null;
  /** Tenant slug from the host, or null on bare apex / marketing / local dev. */
  slug: string | null;
  /** CURRENT pathname. Must be the live value, not a value snapshotted at mount. */
  pathname: string;
  /** From useNeutralBrandingGuard — a client who no longer has a grant with the
   *  firm whose subdomain they are on. */
  suppressHostBranding: boolean;
}): ActiveBranding | null {
  if (input.suppressHostBranding) return null;
  // A firm subdomain is branded on every route, marketing copy included: that is
  // the white-label promise. Only the bare-apex AUTHED fallback is surface-gated.
  if (!input.slug && !isProductSurfacePath(input.pathname)) return null;
  return input.branding;
}

export function TenantBrandingOverlay() {
  const branding = useActiveBranding();
  const slug = resolveHostSlug();

  // ⚠ THE PATHNAME MUST COME FROM THE ROUTER, NOT `window.location` (2026-08-23).
  // This used to read `window.location.pathname` during render, on the grounds
  // that the component should stay mountable without a RouterProvider. But this
  // component renders as a SIBLING of <Outlet/> in RootLayout, and Outlet
  // subscribes to router state on its own — so a client-side navigation
  // re-renders Outlet and NOT this component. The pathname it had read stayed
  // frozen at whatever route the app first booted on, and with it the
  // marketing-neutrality decision below.
  //
  // Concretely: a firm user who lands on elreign.io (no subdomain, a marketing
  // surface, branding correctly suppressed) and then clicks through into the
  // console kept the SUPPRESSED decision for the whole session — the entire
  // authenticated app rendered in default Elreign teal/brass rather than the
  // firm brand, while a hard reload of the same URL rendered it branded. That is
  // the "some screens are on brand, some are not" class, and it is invisible
  // to anyone testing by typing URLs directly.
  //
  // `useLocation` is safe here: this component is only ever rendered by
  // RootLayout, which IS inside the router. The router-free escape hatch the old
  // comment protected is `ApplyBranding` below — that is what __root.test.tsx
  // mounts, and it stays router-free.
  const pathname = useLocation({ select: (l) => l.pathname });

  // Post-firing fallback (decision #4): a CLIENT on a firm subdomain they no
  // longer have an active grant with ("fired the firm" but kept the
  // bookmark) must NOT see that firm's skin, even though the public,
  // grant-unaware by-slug endpoint would happily keep serving it. This is a
  // separate, additive check layered on top of useActiveBranding's result —
  // see use-neutral-branding-guard.ts for why it isn't folded into that hook.
  const suppressHostBranding = useNeutralBrandingGuard();

  // The public marketing site is Elreign's OWN shop window and must never wear
  // a customer's skin. `useActiveBranding` falls back to the SIGNED-IN user's
  // tenant on any host with no subdomain (see its docstring — that fallback
  // exists so app.elreign.io stays branded for a logged-in firm user), and
  // elreign.io is also such a host. So a firm admin who wandered onto the
  // marketing pages repainted them in their own firm's colours, title and
  // favicon — Paul hit exactly this on 2026-08-11 and saw Destinata's favicon
  // on elreign.io. A prospect reading elreign.io while signed into any firm
  // would have seen that firm's brand on our own marketing site.
  //
  // Only the AUTHED fallback is suppressed, and only off the product surfaces:
  // a firm's own subdomain (`slug` set) stays fully branded everywhere,
  // including its login and signup pages, which is the white-label promise.
  const effectiveBranding = resolveEffectiveBranding({
    branding,
    slug,
    pathname,
    suppressHostBranding,
  });

  // Cache the active colours by host slug so the synchronous boot path in
  // main.tsx can pre-paint on the NEXT visit → no flash of Elreign default on
  // a tenant subdomain. (Apex/authed has no stable boot key → one fetch frame.)
  useEffect(() => {
    if (!slug) return;
    if (
      !suppressHostBranding &&
      (effectiveBranding?.primaryColor || effectiveBranding?.accentColor || effectiveBranding?.fontPair)
    ) {
      cacheBranding(`slug:${slug}`, {
        primaryColor: effectiveBranding.primaryColor,
        accentColor: effectiveBranding.accentColor,
        fontPair: effectiveBranding.fontPair,
      });
    } else {
      cacheBranding(`slug:${slug}`, null);
    }
  }, [
    slug,
    suppressHostBranding,
    effectiveBranding?.primaryColor,
    effectiveBranding?.accentColor,
    effectiveBranding?.fontPair,
  ]);

  return <ApplyBranding branding={effectiveBranding} />;
}

interface ApplyBrandingProps {
  branding: ActiveBranding | null;
}

/**
 * Mutates document.documentElement.style + <title> + <link rel="icon"> to
 * reflect the supplied branding. Clears first so switching tenants (or going
 * null) never leaves stale tokens, then derives + applies the firm ramps.
 * Falls back cleanly to the baked Elreign defaults when `branding` is null.
 *
 * Exported so tests can mount it directly with a fixed branding payload
 * (bypassing the network round-trip).
 */
export function ApplyBranding({ branding }: ApplyBrandingProps): null {
  // Original title + favicon captured once on first mount so cleanup / the
  // no-branding path can restore the Elreign defaults. `useRef` (not module
  // state) so tests with multiple mounts each get a fresh capture.
  const originalsRef = useRef<{ title: string; iconHref: string | null } | null>(null);

  const primaryColor = branding?.primaryColor ?? null;
  const accentColor = branding?.accentColor ?? null;
  const firmName = branding?.firmName ?? null;
  const logoUrl = branding?.logoUrl ?? null;
  const fontPair = branding?.fontPair ?? null;

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    const iconLink = () => document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!originalsRef.current) {
      originalsRef.current = {
        title: document.title,
        iconHref: iconLink()?.getAttribute('href') ?? null,
      };
    }

    // Always clear prior overrides first → no cross-tenant token leakage and a
    // clean fall-through to the baked Elreign defaults when nothing applies.
    clearBrandingFromRoot(root);
    if (primaryColor || accentColor || fontPair) {
      applyBrandingToRoot(root, { primaryColor, accentColor, fontPair });
    }

    // Title — "<Firm> · Elreign" when a firm name is set, else the default.
    document.title = firmName ? `${firmName} · Elreign` : originalsRef.current.title;

    // Favicon — swap to the firm logo when supplied, else restore the default.
    const ic = iconLink();
    if (ic) {
      if (logoUrl) ic.setAttribute('href', logoUrl);
      else if (originalsRef.current.iconHref) ic.setAttribute('href', originalsRef.current.iconHref);
    }

    // Cleanup restores defaults (matters for test hermeticity; in production
    // this component lives for the SPA's lifetime).
    return () => {
      clearBrandingFromRoot(root);
      if (originalsRef.current) {
        document.title = originalsRef.current.title;
        const ic2 = iconLink();
        if (ic2 && originalsRef.current.iconHref) ic2.setAttribute('href', originalsRef.current.iconHref);
      }
    };
  }, [primaryColor, accentColor, firmName, logoUrl, fontPair]);

  return null;
}
